"use client";

import React, { useState, useRef, useEffect } from "react";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/shadcn/ui/carousel";
import ChipSerif from "@/components/global/ui/ChipSerif";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ChipStatue from "@/components/global/ui/ChipStatue";

import step1bg from "@/public/home/step1bg.png";
import step1 from "@/public/home/step1.png";
import step2bg from "@/public/home/step2bg.png";
import step2 from "@/public/home/step2.png";
import step3bg from "@/public/home/step3bg.png";
import step3 from "@/public/home/step3.png";
import logo from "@/public/home/logo.svg";
import check from "@/public/home/check.svg";
import { CTAButton } from "@/shadcn/ui/button";
import { usePathname } from "next/navigation";
const listStyles = [
  {
    bg: "bg-[#85D0A6]",
    svgColor: "#85D0A6",
    bgImage: step1bg,
    bgImageClasses: "opacity-[.95]",
    image: step1,
    imageClasses:
      "!w-[30.125rem] lg:!w-[50.625rem] top-0 left-1/2 -translate-x-1/2",
    textBg: "bg-[#79BA96]",
    textWidth: "w-full lg:w-[min(47rem,100%)]",
    listWidth: "w-full lg:w-[min(44.375rem,100%)]",
  },
  {
    bg: "bg-[#93BCDB]",
    svgColor: "#93BCDB",
    bgImage: step2bg,
    bgImageClasses: "opacity-[.46]",
    image: step2,
    imageClasses:
      "!w-[42rem] lg:!w-[71.875rem] bottom-0 left-1/2 -translate-x-1/2",
    textBg: "bg-[#84ACC9]",
    textWidth: "w-full lg:w-[min(47rem,100%)]",
    listWidth: "w-full lg:w-[min(44.375rem,100%)]",
  },
  {
    bg: "bg-[#FFF7E2]",
    svgColor: "#FFF7E2",
    bgImage: step3bg,
    bgImageClasses: "opacity-70",
    image: step3,
    imageClasses:
      "!w-[36rem] lg:!w-[61.875rem] bottom-0 left-1/2 -translate-x-1/2",
    textBg: "bg-[#F4ECD5]",
    textWidth: "w-full lg:w-[min(32.125rem,100%)]",
    listWidth: "w-full lg:w-[min(67rem,100%)]",
  },
];

export default function StepsSection({
  data,
  className,
}: {
  data: PageHomeSchema;
  className?: string;
}) {
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [chipPositions, setChipPositions] = useState<number[]>([]);
  const [maxCardHeight, setMaxCardHeight] = useState<number | null>(null);
  const [headerHeight, setHeaderHeight] = useState<number | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [hasEnoughVerticalSpace, setHasEnoughVerticalSpace] = useState(true);

  const pathname = usePathname();
  const lang =
    pathname.slice(-3) === "/de" || pathname === "/de"
      ? "de"
      : pathname.slice(-3) === "/br" || pathname === "/br"
        ? "br"
        : "en";

  // Calculate chip positions
  useEffect(() => {
    const calculatePositions = () => {
      if (!chipRefs.current.length) return;

      const allChipsMeasured = chipRefs.current.every(
        (chip) => chip && chip.offsetWidth > 0,
      );
      if (!allChipsMeasured) return;

      const positions: number[] = [];
      let cumulativeLeft = 0;

      const remInPixels = parseFloat(
        getComputedStyle(document.documentElement).fontSize,
      );

      chipRefs.current.forEach((chip) => {
        positions.push(cumulativeLeft + 5); // Start at 5rem offset
        if (chip) {
          const widthInPixels = chip.offsetWidth;
          const widthInRem = widthInPixels / remInPixels;
          cumulativeLeft += widthInRem + 1;
        }
      });

      setChipPositions(positions);
    };

    const timeout1 = setTimeout(calculatePositions, 100);
    const timeout2 = setTimeout(calculatePositions, 300);
    const timeout3 = setTimeout(calculatePositions, 500);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(calculatePositions);
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculatePositions, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [data?.stepsItems]);

  // Calculate max card height
  useEffect(() => {
    const calculateMaxHeight = () => {
      if (!cardRefs.current.length || maxCardHeight !== null) return;

      let maxHeightInPixels = 0;

      cardRefs.current.forEach((card) => {
        if (card) {
          const height = card.offsetHeight;
          if (height > maxHeightInPixels) {
            maxHeightInPixels = height;
          }
        }
      });

      if (maxHeightInPixels > 0) {
        const remInPixels = parseFloat(
          getComputedStyle(document.documentElement).fontSize,
        );
        const maxHeightInRem = maxHeightInPixels / remInPixels;
        setMaxCardHeight(maxHeightInRem);
      }
    };

    const timeout1 = setTimeout(calculateMaxHeight, 150);
    const timeout2 = setTimeout(calculateMaxHeight, 400);
    const timeout3 = setTimeout(calculateMaxHeight, 800);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setTimeout(calculateMaxHeight, 100);
      });
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateMaxHeight, 200);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [data?.stepsItems, maxCardHeight]);

  // Calculate header height
  useEffect(() => {
    const calculateHeaderHeight = () => {
      if (!headerRef.current) return;

      const heightInPixels = headerRef.current.offsetHeight;
      if (heightInPixels > 0) {
        const remInPixels = parseFloat(
          getComputedStyle(document.documentElement).fontSize,
        );
        const heightInRem = heightInPixels / remInPixels;
        setHeaderHeight(heightInRem);
      }
    };

    const timeout1 = setTimeout(calculateHeaderHeight, 150);
    const timeout2 = setTimeout(calculateHeaderHeight, 400);
    const timeout3 = setTimeout(calculateHeaderHeight, 800);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setTimeout(calculateHeaderHeight, 100);
      });
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateHeaderHeight, 200);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [headerHeight]);

  // Track screen size
  useEffect(() => {
    const handleResize = () => {
      const isLg = window.innerWidth >= 993;
      setIsLargeScreen(isLg);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Check if there's enough vertical space
  useEffect(() => {
    const checkVerticalSpace = () => {
      if (!isLargeScreen || headerHeight === null || maxCardHeight === null) {
        setHasEnoughVerticalSpace(true);
        return;
      }

      const remInPixels = parseFloat(
        getComputedStyle(document.documentElement).fontSize,
      );
      const headerOffsetRem = 3;
      const totalRequiredHeight =
        (headerOffsetRem + headerHeight + maxCardHeight) * remInPixels;
      const totalWithBuffer = totalRequiredHeight + remInPixels;

      const hasSpace = window.innerHeight >= totalWithBuffer;
      setHasEnoughVerticalSpace(hasSpace);
    };

    checkVerticalSpace();
    window.addEventListener("resize", checkVerticalSpace);

    return () => {
      window.removeEventListener("resize", checkVerticalSpace);
    };
  }, [headerHeight, maxCardHeight, isLargeScreen]);

  return (
    <section
      className={cn(
        "relative overflow-clip pb-[7rem] lg:pb-[22rem] lg:pt-[5rem]",
        className,
      )}
    >
      <div className="container flex flex-col items-center">
        <div
          ref={headerRef}
          className={cn(
            "flex flex-col items-center mb-[8rem] lg:mb-[12.5rem]",
            hasEnoughVerticalSpace && "lg:sticky lg:top-[6rem]",
          )}
        >
          <ChipSerif text={data?.stepsChip} />

          {data?.stepsTitle && (
            <div className="richtext prose-h2:w-[35rem] sm:prose-h2:w-auto prose-strong:font-semibold prose-p:w-[min(68.875rem,100%)]">
              <PortableText
                value={data.stepsTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {data?.stepsItems && (
          <div className="w-[min(198.375rem,100%)] flex flex-col gap-[10rem] lg:gap-[25rem] mx-auto">
            {data.stepsItems.map((item, index) => (
              <div
                key={index}
                className="lg:sticky"
                style={{
                  ...(isLargeScreen && {
                    top:
                      hasEnoughVerticalSpace && headerHeight !== null
                      ? `${headerHeight + 6 + 12.5}rem`
                      : "10rem",
                  }),
                }}
              >
                <div
                  ref={(el) => (cardRefs.current[index] = el)}
                  className={cn(
                    "flex flex-col lg:flex-row items-center rounded-[2rem] lg:rounded-[4rem] pt-[6rem] lg:py-[10rem] lg:px-[8rem] relative",
                    index % 2 === 1 ? "lg:flex-row-reverse" : "",
                    listStyles[index % listStyles.length].bg,
                  )}
                  style={{
                    ...(maxCardHeight !== null &&
                      isLargeScreen && {
                        height: `${maxCardHeight}rem`,
                      }),
                  }}
                >
                  <div
                    ref={(el) => (chipRefs.current[index] = el)}
                    className={cn(
                      "absolute bottom-full translate-y-px rounded-t-[2.5rem]",
                      listStyles[index % listStyles.length].bg,
                    )}
                    style={{
                      ...(chipPositions[index] !== undefined &&
                        isLargeScreen && {
                          left: `${chipPositions[index]}rem`,
                        }),
                      ...(!isLargeScreen && {
                        left: "5rem",
                      }),
                    }}
                  >
                    <div className="relative w-full h-full">
                      <svg
                        className="absolute right-full translate-x-px translate-y-px -scale-x-[100%] bottom-px w-[1.5rem] h-[2rem]"
                        viewBox="0 0 2 5"
                        xmlns="http://www.w3.org/2000/svg"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M 0 5 V 0 A 10 10 0 0 0 2 5 Z"
                          fill={listStyles[index % listStyles.length].svgColor}
                        />
                      </svg>
                      <svg
                        className="absolute bottom-px left-full -translate-x-px translate-y-px w-[1.5rem] h-[2rem]"
                        viewBox="0 0 2 5"
                        xmlns="http://www.w3.org/2000/svg"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M 0 5 V 0 A 10 10 0 0 0 2 5 Z"
                          fill={listStyles[index % listStyles.length].svgColor}
                        />
                      </svg>

                      <div className="flex relative justify-between items-center w-full h-full gap-[5rem] py-[.5rem] pl-[2rem] pr-[.625rem] lg:gap-[9rem]">
                        <p className="relative mb-0 font-medium tracking-tight leading-none uppercase text-[#103037] text-[1.75rem]">
                          {item?.tabTitle}
                        </p>

                        <p className="flex relative justify-center items-center mb-0 font-medium tracking-tight leading-none text-white rounded-full bg-[#103037] min-h-[3.75rem] px-[2rem] text-[1.75rem] lg:min-h-[4rem] lg:text-[2rem]">
                          {lang !== "br" ? "STEP" : "PASSO"} {index + 1}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="px-[2rem] sm:px-[4rem] lg:px-0 w-[min(117rem,100%)] relative z-10">
                    <div className="flex justify-center items-center lg:justify-start mb-[3rem] gap-[1.75rem] lg:mb-[4.5rem]">
                      <Image
                        className="!w-[4rem] flex-shrink-0"
                        src={logo}
                        alt=""
                      />
                      <p className="mb-0 font-medium tracking-tight leading-none font-geist text-[3.5rem] text-[#103037]">
                        {item.title}
                      </p>
                    </div>

                    <div
                      className={cn(
                        "flex flex-col lg:flex-row gap-[3rem] lg:justify-between items-center rounded-[1.5rem] lg:rounded-[2.75rem] py-[3rem] lg:py-[5rem] px-[3rem] sm:px-[5rem] lg:px-[6rem]",
                        listStyles[index % listStyles.length].textBg,
                      )}
                    >
                      <div
                        className={cn(
                          "",
                          listStyles[index % listStyles.length].textWidth,
                        )}
                      >
                        <div className="richtext richtext-start prose-h3:font-bold prose-h3:text-[2.75rem] prose-h3:text-[#103037] prose-p:text-[#103037]/60">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      </div>

                      <div className="w-full h-px lg:self-stretch lg:w-px lg:h-auto bg-[#103037]/10" />

                      <div
                        className={cn(
                          "",
                          listStyles[index % listStyles.length].listWidth,
                        )}
                      >
                        {item?.listTitle && (
                          <p className="font-medium mb-[2rem] text-[2.25rem] text-[#103037]">
                            {item.listTitle}
                          </p>
                        )}

                        {item?.list && (
                          <ul className="flex flex-col items-start gap-[2.25rem]">
                            {item.list.map((listItem, listIndex) => (
                              <li
                                key={listIndex}
                                className="flex items-center gap-[1rem]"
                              >
                                <Image
                                  className="!w-[2.25rem] flex-shrink-0"
                                  src={check}
                                  alt="check icon"
                                />

                                <div className="richtext richtext-start prose-p:text-[#103037]/60">
                                  <PortableText
                                    value={listItem.text}
                                    components={richTextComponent}
                                  />
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    <p className="mb-0 uppercase text-[#103037]/60 text-[1.75rem] mt-[3rem]">
                      {item?.notice}
                    </p>
                  </div>

                  <div
                    className={cn(
                      "relative lg:absolute lg:top-0 aspect-[639/558] h-auto lg:h-full w-full lg:w-auto pointer-events-none rounded-[2rem] lg:rounded-[4rem] overflow-clip",
                      index % 2 === 0 ? "right-0" : "left-0",
                    )}
                  >
                    <div className="relative w-full h-full">
                      <Image
                        className={cn(
                          "!w-full !h-full object-cover",
                          listStyles[index % listStyles.length].bgImageClasses,
                          "[mask-image:linear-gradient(to_top,black_20%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_top,black_20%,transparent_100%)]",
                          index % 2 === 0
                            ? "lg:[mask-image:linear-gradient(to_left,black_0%,transparent_100%)] lg:[-webkit-mask-image:linear-gradient(to_left,black_0%,transparent_100%)]"
                            : "lg:[mask-image:linear-gradient(to_right,black_0%,transparent_100%)] lg:[-webkit-mask-image:linear-gradient(to_right,black_0%,transparent_100%)]",
                        )}
                        src={listStyles[index % listStyles.length].bgImage}
                        alt=""
                      />

                      <Image
                        className={cn(
                          "absolute pointer-events-none",
                          listStyles[index % listStyles.length].imageClasses,
                        )}
                        src={listStyles[index % listStyles.length].image}
                        alt=""
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {data?.stepsCta && (
        <div className="flex relative z-10 justify-center pt-[4rem] lg:pt-[6rem] lg:px-0 px-[2rem]">
          <CTAButton data={data.stepsCta} />
        </div>
      )}
    </section>
  );
}
